import { useCallback, useEffect, useState } from 'react';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Button, Dialog, DialogContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { isPdf } from '../utils';

interface FileCarouselDialogProps {
  allFiles: string[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

const FileCarouselDialog: React.FC<FileCarouselDialogProps> = ({ allFiles, initialIndex = 0, open, onClose }) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % allFiles.length);
  }, [allFiles.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + allFiles.length) % allFiles.length);
  }, [allFiles.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (open) {
        if (e.key === 'ArrowRight') {
          handleNext();
        } else if (e.key === 'ArrowLeft') {
          handlePrev();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleNext, handlePrev]);

  const currentFile = allFiles[currentIndex] || '';
  const isCurrentPdf = isPdf(currentFile);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' },
      }}
    >
      <DialogContent style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isCurrentPdf ? (
          <iframe
            src={currentFile}
            title="PDF Viewer"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <img src={currentFile} alt="Selected" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        )}

        <Button
          onClick={handlePrev}
          disabled={allFiles.length <= 1}
          style={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: theme.palette.red.main,
            fontSize: '4rem',
          }}
        >
          <ArrowBackIosIcon fontSize="inherit" />
        </Button>

        <Button
          onClick={handleNext}
          disabled={allFiles.length <= 1}
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: theme.palette.red.main,
            fontSize: '4rem',
          }}
        >
          <ArrowForwardIosIcon fontSize="inherit" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default FileCarouselDialog;
